import { useEffect, useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useAnalyticsOrgLocations, DemoChip, LocationChip } from '@frontend/analytics-shared';
import { BarChartData, Chart, FormatValue, centerPosition } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import { treatmentPlanChartContainerStyle } from './common-styles';
import {
  calcAverageIndustryPercentChange,
  getIndustryAverageComparisonText,
} from './helpers/industry-average-comparison-text';
import { getPracticeAnalyticsQueryVariables } from './helpers/query-variables';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { useMonthlyTrendChartModal } from './monthly-trend-chart-modal';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      restorativeTreatmentPlan: treatmentPlan(treatmentPlanType: RESTORATIVE) {
        totals(start: $start, end: $end, step: $step) {
          accepted
          acceptedPercent
          diagnosed
          diagnosedPercent
          qualifiedVisits
          unscheduledTreatment
        }
        benchmarks {
          label
          value
        }
      }
      industryAvg {
        totals(start: $start, end: $end, step: $step) {
          totalAcceptedRestorative
          totalDiagnosedRestorative
          totalUnscheduledTreatmentRestorative
        }
      }
    }
  }
`;

type IndustryAveragePercentChange = {
  patientsAccepted?: number;
  patientsDiaglosed?: number;
  unscheduledProduction?: number;
};

type ChartDataState = {
  avgAccepted: number;
  avgUnscheduledProduction: number;
  charts: {
    aggregated?: {
      patientsAccepted?: BarChartData;
      patientsDiaglosed?: BarChartData;
      unscheduledProduction?: BarChartData;
    };
    categorized?: {
      [groupName: string]: {
        [barId: string]: number;
      };
    };
  };
  industryAveragePercentChange: IndustryAveragePercentChange;
  patientsAccepted: number;
  patientsAcceptedPercent: number;
  patientsDiaglosed: number;
  patientsDiaglosedPercent: number;
  topAccepted: number;
  topDiagnosed: number;
  unscheduledProduction: number;
};

const colors = {
  activePractice: theme.colors.warning50,
  avgPractice: theme.colors.primary20,
  patientsAccepted: theme.colors.warning50,
  patientsDiagnosed: theme.colors.warning50,
  topPractice: theme.colors.secondary.seaweed30,
  unscheduledProduction: theme.colors.warning50,
};

export const RestorativeTreatmentMetric = ({
  clickNoop,
  isDrillDownPage,
  onFetchStateChange,
}: PracticeAnalyticsTypes.MetricProps) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const navigate = usePaNavigate();
  const { contentWidth, isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });

  const monthlyTrendModal = useMonthlyTrendChartModal({
    defaultKey: 'diagnosedPercent',
    metric: 'restorativeTreatmentPlan',
    tabs: { diagnosedPercent: t('Diagnosed %'), acceptedPercent: t('Accepted %') },
    title: t('Trend: Restorative Treatment Plan'),
    trackingId: trackingIds.practiceAnalytics.restorativeTreatmentPlanTrend,
  });

  const multipleLocationsSelected = (filters.locations?.length || 0) > 1;
  const showMultiComparison = isDrillDownPage && multipleLocationsSelected;

  const labels = useMemo(
    () => ({
      activePractice: multipleLocationsSelected ? t('Your Avg Practice') : t('Your Practice'),
      avgPractice: t('Average Practice'),
      groupNameHeader: t('Location'),
      patientsAccepted: t('Patients Accepted'),
      patientsDiagnosed: t('Patients Diagnosed'),
      topPractice: t('Top Practice'),
      unscheduledProduction: t('Unscheduled Revenue'),
    }),
    [multipleLocationsSelected, t]
  );

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `restorativeTreatmentSummary-${isDemoAccount}-${JSON.stringify(filters)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.RestorativeTreatmentPlanResponse>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.restorativeTreatmentPlan : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const renderChart = (chartName: string, data?: BarChartData, formatValue?: FormatValue) => (
    <div css={centerPosition}>
      <Chart.BarChart
        appearance={{
          barsGap: isSmallMode ? (contentWidth <= 470 ? 8 : 16) : 24,
          collectiveTooltip: true,
          height: 180,
          showScrollControls: false,
        }}
        customLegendsIds={['activePractice', 'avgPractice', 'topPractice']}
        data={data}
        formatValue={formatValue}
      />
      <Text color='subdued' size='medium' textAlign='center' style={{ marginTop: theme.spacing(1) }}>
        {chartName}
      </Text>
    </div>
  );

  const chartData: ChartDataState | null = useMemo(() => {
    const { aggregatedData, rawData } = PracticeAnalyticsAggregations.restorativeTreatmentPlanSummary(data);

    if (!aggregatedData) {
      return null;
    }

    const { industryAvg, restorativeTreatmentPlan } = aggregatedData;
    const averageWeaveAccepted = Math.round(
      ((industryAvg?.totals?.totalAcceptedRestorative || 0) / (industryAvg?.totals?.totalDiagnosedRestorative || 1)) *
        100
    );

    const topAccepted =
      (restorativeTreatmentPlan.benchmarks?.find(({ label }) => label === 'Top Accepted')?.value || 0) * 100;
    const topDiagnosed =
      (restorativeTreatmentPlan.benchmarks?.find(({ label }) => label === 'Top Diagnosed')?.value || 0) * 100;

    const processedData: ChartDataState = {
      avgAccepted: averageWeaveAccepted,
      avgUnscheduledProduction: industryAvg?.totals?.totalUnscheduledTreatmentRestorative || 0,
      charts: {},
      industryAveragePercentChange: {
        patientsAccepted:
          Math.round((restorativeTreatmentPlan?.totals?.acceptedPercent || 0) * 100) - averageWeaveAccepted,
        patientsDiaglosed: calcAverageIndustryPercentChange({
          industryAverage: industryAvg?.totals?.totalDiagnosedRestorative,
          practiceValue: restorativeTreatmentPlan?.totals?.diagnosed,
        }),
        unscheduledProduction: calcAverageIndustryPercentChange({
          industryAverage: industryAvg?.totals?.totalUnscheduledTreatmentRestorative,
          practiceValue: restorativeTreatmentPlan?.totals?.unscheduledTreatment,
        }),
      },
      patientsAccepted: restorativeTreatmentPlan?.totals?.accepted || 0,
      patientsAcceptedPercent: restorativeTreatmentPlan?.totals?.acceptedPercent || 0,
      patientsDiaglosed: restorativeTreatmentPlan?.totals?.diagnosed || 0,
      patientsDiaglosedPercent: restorativeTreatmentPlan?.totals?.diagnosedPercent || 0,
      topAccepted,
      topDiagnosed,
      unscheduledProduction: restorativeTreatmentPlan?.totals?.unscheduledTreatment || 0,
    };

    if (showMultiComparison) {
      processedData.charts.categorized = Object.entries(rawData || {}).reduce((acc, [locationId, locationDetails]) => {
        acc![locationNames[locationId] || locationId] = {
          patientsDiagnosed: (locationDetails?.location.restorativeTreatmentPlan?.totals?.diagnosedPercent || 0) * 100,
          patientsAccepted: (locationDetails?.location.restorativeTreatmentPlan?.totals?.acceptedPercent || 0) * 100,
          unscheduledProduction: locationDetails?.location.restorativeTreatmentPlan?.totals?.unscheduledTreatment || 0,
        };
        return acc;
      }, {} as ChartDataState['charts']['categorized']);
    } else {
      processedData.charts.aggregated = {
        patientsAccepted: {
          groups: [
            {
              name: t('Patients Accepted'),
              values: {
                activePractice: (restorativeTreatmentPlan?.totals?.acceptedPercent || 0) * 100,
                avgPractice: averageWeaveAccepted,
                topPractice: topAccepted,
              },
            },
          ],
        },
        patientsDiaglosed: {
          groups: [
            {
              name: t('Patients Diagnosed'),
              values: {
                activePractice: (restorativeTreatmentPlan?.totals?.diagnosedPercent || 0) * 100,
                topPractice: topDiagnosed,
              },
            },
          ],
        },
        unscheduledProduction: {
          groups: [
            {
              name: t('Unscheduled Revenue'),
              values: {
                activePractice: restorativeTreatmentPlan?.totals?.unscheduledTreatment || 0,
                avgPractice: industryAvg?.totals?.totalUnscheduledTreatmentRestorative || 0,
              },
            },
          ],
        },
      };
    }

    return processedData;
  }, [locationNames, data, showMultiComparison, t]);

  useEffect(() => {
    onFetchStateChange?.(isLoading);
  }, [isLoading, onFetchStateChange]);

  return (
    <>
      <Chart
        colors={colors}
        isLoading={isLoading}
        labels={labels}
        noBorder={!isDrillDownPage && isSmallMode}
        onClick={
          clickNoop
            ? undefined
            : () => {
                navigate({
                  to: `/analytics/practice/restorative-treatment`,
                });
              }
        }
        style={!isDrillDownPage && isSmallMode ? { marginBottom: 0 } : undefined}
        trackingId={trackingIds.practiceAnalytics.restorativeTreatmentPlanChart}
      >
        {isDrillDownPage ? (
          isSmallMode && (
            <Chart.Header
              title={t('Treatment Acceptance vs. Missed Revenue')}
              leftElement={showDemoChipAndBanner && <DemoChip />}
            />
          )
        ) : (
          <>
            {!isSmallMode && (
              <Chart.Header
                actions={[monthlyTrendModal.triggerProps]}
                bottomElement={
                  multipleLocationsSelected ? (
                    <CompareLocationsButton style={{ marginBottom: theme.spacing(1) }} />
                  ) : null
                }
                infoTip={<PracticeAnalyticsInfoTips tip='restorativeTreatmentPlan' />}
                leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
                title={t('Restorative Treatment Plan')}
              />
            )}
            <Chart.SneakPeakBar
              data={[
                isSmallMode
                  ? null
                  : {
                      key: 'patientsDiagnosed',
                      name: t('Patients Diagnosed'),
                      value: formatters.percent(chartData?.patientsDiaglosedPercent || 0),
                    },
                {
                  key: 'patientsAccepted',
                  name: t('Patients Accepted'),
                  omitSubtext: multipleLocationsSelected,
                  subtext: getIndustryAverageComparisonText(chartData?.industryAveragePercentChange?.patientsAccepted),
                  value: formatters.percent(chartData?.patientsAcceptedPercent || 0),
                },
                {
                  key: 'unscheduledProduction',
                  name: t('Unscheduled Treatment'),
                  omitSubtext: multipleLocationsSelected,
                  subtext: getIndustryAverageComparisonText(
                    chartData?.industryAveragePercentChange?.unscheduledProduction
                  ),
                  value: isSmallMode
                    ? formatters.shortenCurrency(chartData?.unscheduledProduction)
                    : formatters.currency(chartData?.unscheduledProduction),
                },
              ]}
            />
          </>
        )}

        {showMultiComparison ? (
          <>
            <Chart.CategoryBarChart
              appearance={{
                customTooltipTitle: ({ groupName }) => <LocationChip locationName={groupName} noTruncating />,
                formatters: {
                  patientsAccepted: formatters.appendPercent,
                  patientsDiagnosed: formatters.appendPercent,
                  unscheduledProduction: (v) => formatters.currency(v),
                },
                maxValues: {
                  patientsAccepted: 100,
                  patientsDiagnosed: 100,
                },
                xAxisTickFormatters: {
                  patientsAccepted: formatters.appendPercent,
                  patientsDiagnosed: formatters.appendPercent,
                  unscheduledProduction: (value) => formatters.shortenCurrency(value),
                },
              }}
              data={chartData?.charts.categorized || {}}
              markers={{
                patientsDiagnosed: [
                  {
                    color: theme.colors.primary40,
                    id: 'avgPractice',
                    value: chartData?.topDiagnosed || 0,
                  },
                ],
                patientsAccepted: [
                  {
                    id: 'topPractice',
                    value: chartData?.topAccepted || 0,
                  },
                  {
                    color: theme.colors.primary40,
                    id: 'avgPractice',
                    value: chartData?.avgAccepted || 0,
                  },
                ],
                unscheduledProduction: [
                  {
                    color: theme.colors.primary40,
                    id: 'avgPractice',
                    value: chartData?.avgUnscheduledProduction || 0,
                  },
                ],
              }}
            />
            <Chart.Legends
              customData={{
                activePractice: {
                  label: labels['activePractice'],
                },
                avgPractice: {
                  label: labels['avgPractice'],
                },
                topPractice: {
                  label: labels['topPractice'],
                },
              }}
            />
          </>
        ) : (
          <>
            <div css={treatmentPlanChartContainerStyle}>
              {!isSmallMode &&
                renderChart(
                  t('Patients Diagnosed'),
                  chartData?.charts.aggregated?.patientsDiaglosed,
                  formatters.appendPercent
                )}

              {renderChart(
                t('Patients Accepted'),
                chartData?.charts.aggregated?.patientsAccepted,
                formatters.appendPercent
              )}

              {renderChart(t('Unscheduled Treatment'), chartData?.charts.aggregated?.unscheduledProduction, (v) =>
                formatters.currency(v)
              )}
            </div>
            <Chart.Legends style={{ marginTop: theme.spacing(1) }} />
          </>
        )}
      </Chart>
      {monthlyTrendModal.modalRenderer()}
    </>
  );
};
